import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React from "react";
import autoBind from "react-autobind";
import shadowRoot from "react-shadow";
import PinButton from "~/components/drawers/timeline/PinButton";
import Tooltipable from "~/components/effects/tooltipable";
import ActivityEditor from "~/components/forms/HtmlEditors/ActivityEditor";
import GlobalContainer from "~/components/global_container";
import UnsupportedVariablesWarning from "~/components/modals/UnsupportedVariablesWarning";
import Avatar from "~/components/shared/Avatar";
import CopyLink from "~/components/shared/CopyLink";
import { formatEmailReplyBody } from "~/constants/TinyMCE";
import AppDispatcher from "~/dispatchers/app_dispatcher";
import { getCcRecipientAddresses } from "~/helpers/Emails/recipients";
import APIRequest from "~/lib/api_request";
import brokerbit from "~/lib/brokerbit";
import dayjs from "~/lib/dayjs";
import {
  isUnsupportedVariablesError,
  normalizeUnsupportedVariablesError,
} from "~/lib/unsupportedVariables";
import LeadDrawerStore from "~/stores/lead_drawer_store";
import LeadDrawerActionTypes from "~/types/lead_drawer_action_types";

class TimelineActivity extends React.Component {
  containerRef = React.createRef();

  constructor(props) {
    super(props);

    this.state = {
      lead: props.lead,
      activity: props.activity,
      queuedChanges: {},
      loading: true, // Used in Activities
      emailEvents: {}, // Used in Email Activities
      editing: false,
      variablesWarning: null,
      submittingSaveAnyway: false,
    };

    autoBind(this);

    this.onSubmit = this.onSubmit.bind(this); // don't remove, for some reason autoBind doesn't work with onSubmit: https://zube.io/brokerkit/webapp/c/1920
    this.onDelete = this.onDelete.bind(this);
    this.onLeadDrawerStoreChange = this.onLeadDrawerStoreChange.bind(this);
    this.handleReply = this.createReplyHandler(false);
    this.handleReplyAll = this.createReplyHandler(true);
  }

  componentDidMount() {
    this.leadDrawerStoreListener = LeadDrawerStore.addListener(
      this.onLeadDrawerStoreChange,
    );
  }

  componentDidUpdate(prevProps) {
    const { activity } = this.props;
    if (activity !== prevProps.activity) {
      this.setState({ activity });
    }
  }

  componentWillUnmount() {
    if (this.leadDrawerStoreListener) {
      this.leadDrawerStoreListener.remove();
    }

    clearTimeout(this.deleteTimeout);
  }

  onLeadDrawerStoreChange() {
    const leadDrawerState = LeadDrawerStore.getState();
    const { activity: currentActivity } = this.state;
    const { activity: leadDrawerActivity, leadDrawerStoreAction } =
      leadDrawerState;

    if (
      leadDrawerStoreAction === "updateLeadActivityDone" &&
      currentActivity?.id === leadDrawerActivity?.id
    ) {
      this.setState({ activity: leadDrawerActivity });
    }
  }

  handleEditClick = () => {
    this.setState({ editing: true });
  };

  handleFormClose = () => {
    this.setState({ editing: false });
  };

  onSubmit(e, saveAnyway = false) {
    e.preventDefault();

    const { lead, activity, queuedChanges } = this.state;
    this.setState({ sending: true });

    APIRequest.put({
      resource: `/v1/leads/${lead.id}/activities/${activity.id}`,
      data: {
        save_anyway: saveAnyway,
        activity: {
          body: queuedChanges?.body,
          parameters: queuedChanges?.parameters,
        },
      },
    }).end((err, res) => {
      if (err) {
        if (isUnsupportedVariablesError(res?.body)) {
          this.setState({
            variablesWarning: normalizeUnsupportedVariablesError(res.body),
            submittingSaveAnyway: false,
            sending: false,
          });
          return;
        }

        this.setState({
          variablesWarning: null,
          submittingSaveAnyway: false,
          sending: false,
        });
        GlobalContainer.notify(
          "Failed to save activity. Please try again.",
          "error",
        );
        return;
      }

      this.setState({
        activity: res.body,
        variablesWarning: null,
        submittingSaveAnyway: false,
        sending: false,
        editing: false,
      });
      this.handleFormClose();
    });
  }

  async onDelete(e) {
    e.preventDefault();

    const { deleteMessage, removeActivity } = this.props;
    const { lead, activity } = this.state;
    const message = deleteMessage || "Are you sure you want to delete this?";

    const confirmed = await this.confirmDeletion(message);
    if (!confirmed) {
      return;
    }

    this.setState({ deleting: true });
    try {
      await this.deleteActivity(lead, activity);
      this.deleteTimeout = setTimeout(() => {
        this.setState({
          deleting: false,
          deleted: true,
        });

        if (removeActivity) {
          removeActivity(activity.id);
        }
      }, 500);
    } catch (err) {
      this.setState({ deleting: false, deleted: false });
    }
  }

  confirmDeletion = (message) =>
    new Promise((resolve) => {
      brokerbit.confirmBox({
        message,
        callback: (ok) => resolve(ok),
      });
    });

  deleteActivity = (lead, activity) =>
    APIRequest.delete({
      resource: `/v1/leads/${lead.id}/activities/${activity.id}`,
    });

  createReplyHandler = (isReplyAll) => (e) => {
    if (e) {
      e.preventDefault();
    }
    const { activity, lead } = this.state;
    const { setCurrentFormWithData } = this.props;
    if (!setCurrentFormWithData || !activity.email || !lead.email) {
      return;
    }

    const currentSubject = activity.email.subject || "";
    const cleanSubject = currentSubject.replace(/^(?:Fwd|FWD|Fw|FW): /g, "");
    const replySubject = cleanSubject.startsWith("Re: ")
      ? cleanSubject
      : `Re: ${cleanSubject}`;

    const replyBody = formatEmailReplyBody({
      body: activity.email.body,
      senderName: activity.email.sender_name,
      senderEmail: activity.email.sender_email,
      createdAt: activity.email.created_at,
    });

    const emailProps = {
      subject: replySubject,
      body: replyBody,
      cc_recipients: isReplyAll
        ? getCcRecipientAddresses(activity.email.email_recipients)
        : [],
      attachments: [],
      references: activity.email.references || [],
    };

    setCurrentFormWithData({ form: "email", data: { object: emailProps } });
  };

  switchActivityView(value) {
    const { lead, activity } = this.state;
    const { toggleActivityInternal } = this.props;

    APIRequest.put({
      resource: `/v1/leads/${lead.id}/activities/${activity.id}`,
      data: {
        activity: { internal: value === "lock" },
      },
    }).end((err, res) => {
      if (err || res?.body?.errors) {
        return;
      }

      const updatedActivity = res.body;
      this.setState({ activity: updatedActivity });

      if (toggleActivityInternal) {
        toggleActivityInternal(updatedActivity);
      }

      AppDispatcher.dispatch({
        type: LeadDrawerActionTypes.TOGGLE_ACTIVITY_INTERNAL,
        activity: {
          ...updatedActivity,
          id: activity.id,
          internal: value === "lock",
        },
      });
    });
  }

  renderPinButton() {
    const { activity } = this.state;
    const {
      isPinned,
      isAtLimit,
      pinnedCount,
      onPinToggle,
      canPin,
      canUnpin,
      isPinLoading,
    } = this.props;

    // Don't render pin button if prop callbacks not provided (not in pin-enabled context)
    if (!onPinToggle) {
      return null;
    }

    return (
      <PinButton
        isPinned={!!isPinned}
        isAtLimit={!!isAtLimit}
        pinnedCount={pinnedCount || 0}
        onToggle={() => onPinToggle(activity)}
        canPin={canPin !== undefined ? canPin : true}
        canUnpin={canUnpin !== undefined ? canUnpin : true}
        isLoading={!!isPinLoading}
      />
    );
  }

  renderReplyButtons() {
    const { lead } = this.state;
    const leadEmail = lead.email;
    const hasLeadEmail = !!leadEmail;

    return (
      <>
        <Tooltipable
          text={!hasLeadEmail ? "Contact's email required" : null}
          className=""
        >
          <button
            type="button"
            onClick={this.handleReply}
            className="btn btn-secondary btn-sm mr5"
            disabled={!hasLeadEmail}
          >
            <FontAwesomeIcon icon={["far", "fa-reply"]} />
          </button>
        </Tooltipable>

        <Tooltipable
          text={!hasLeadEmail ? "Contact's email required" : null}
          className=""
        >
          <button
            type="button"
            onClick={this.handleReplyAll}
            className="btn btn-secondary btn-sm mr5"
            disabled={!hasLeadEmail}
          >
            <FontAwesomeIcon icon={["far", "fa-reply-all"]} />
          </button>
        </Tooltipable>
      </>
    );
  }

  renderForm() {
    const { activity, lead, queuedChanges, sending } = this.state;
    const activityType = activity.key?.split(".").pop();

    return (
      <form onSubmit={this.onSubmit}>
        <ActivityEditor
          value={activity.body}
          onChange={(html) => {
            this.setState((prevState) => ({
              queuedChanges: { ...prevState.queuedChanges, body: html },
            }));
          }}
          placeholder="Enter Text"
          activityType={activityType}
          leadId={lead?.id}
        />
        <div className="mt5">
          <button
            type="submit"
            className="btn btn-primary btn-sm mr5"
            disabled={sending}
          >
            {sending ? (
              <span>
                <FontAwesomeIcon icon="far fa-spinner" pulse className="mr5" />{" "}
                Saving
              </span>
            ) : (
              "Save"
            )}
          </button>
          <a
            href="#"
            onClick={() => {
              this.setState({ queuedChanges: {}, editing: false });
              this.handleFormClose();
            }}
            className="btn btn-secondary btn-sm"
          >
            Cancel
          </a>
        </div>
        {this.renderUnsupportedVariablesWarning()}
      </form>
    );
  }

  renderUnsupportedVariablesWarning() {
    return (
      <UnsupportedVariablesWarning
        show={!!this.state.variablesWarning}
        warning={this.state.variablesWarning}
        saving={this.state.submittingSaveAnyway}
        onReview={() => this.setState({ variablesWarning: null })}
        onSaveAnyway={() => {
          this.setState({
            variablesWarning: null,
            submittingSaveAnyway: true,
            sending: true,
          });
          this.onSubmit({ preventDefault: () => false }, true);
        }}
      />
    );
  }

  renderView() {
    const { activity } = this.state;

    if (!activity) {
      return null;
    }

    return (
      <>
        {activity.body && (
          <shadowRoot.div className="form-control font-activity">
            <div
              className="content"
              dangerouslySetInnerHTML={{
                __html: activity.body || "",
              }}
            />
          </shadowRoot.div>
        )}
      </>
    );
  }

  renderWhoTimestamp() {
    const { activity } = this.state;

    return (
      <div className="text-grey mt10">
        <span className="pull-right text-grey">
          <Tooltipable
            text={dayjs(activity.occurred_at).format("MMMM Do YYYY, h:mm:ss a")}
          >
            <>{dayjs(activity.occurred_at).fromNow()}</>
          </Tooltipable>
        </span>
        <>
          <Avatar
            name={activity.owner?.name}
            avatar={activity.owner?.avatar_url}
            sizeType="tiny"
          />{" "}
          {activity.owner?.name}
        </>
      </div>
    );
  }

  renderActivitySwitch() {
    const { activity } = this.state;
    const { alwaysPublic } = this.props;

    if (alwaysPublic) {
      return null;
    }

    return this.renderActivityButton(activity.internal);
  }

  renderActivityButton(isInternal) {
    const { editable, deletable } = this.props;

    const buttonClass = classNames("btn btn-secondary btn-sm pl8 pr8", {
      mr5: editable || deletable,
    });

    const tooltipText = isInternal
      ? "Private - Only visible to Admin or Staff users and not Agent/Affiliate role users"
      : "Public - Visible to all user roles";
    const icon = isInternal ? ["far", "fa-eye-slash"] : ["far", "fa-eye"];

    return (
      <Tooltipable text={tooltipText}>
        <button
          type="button"
          className={buttonClass}
          onClick={(e) =>
            this.switchActivityView(isInternal ? "unlock" : "lock")
          }
        >
          <FontAwesomeIcon icon={icon} />
        </button>
      </Tooltipable>
    );
  }

  renderDetailsButton() {
    return null; // Override in child classes if needed
  }

  render() {
    const {
      editable,
      deletable,
      title,
      icon,
      prefix,
      color,
      form,
      view,
      children,
      content,
      showReplyButtons,
      activeTab,
    } = this.props;
    const { deleting, deleted, editing, activity, lead } = this.state;

    if (deleted) {
      return null;
    }

    let body;
    if (children) {
      body = children;
    } else if (editing) {
      body = this.renderForm();
    } else {
      let _title;
      if (typeof title === "function") {
        _title = title(this);
      } else {
        _title = title;
      }

      body = (
        <div>
          <div className="d-flex justify-content-between align-items-center mb5">
            {_title && (
              <div className="d-flex align-items-center">
                <h5 className="text-grey-dark mb-0">{_title}</h5>
                {this.renderDetailsButton()}
              </div>
            )}

            <div className="d-flex ml-5">
              <CopyLink activity={activity} leadId={lead.id} tab={activeTab} />
              {this.renderPinButton()}
              {showReplyButtons && this.renderReplyButtons()}
              {Rails.abilities.manageLead && this.renderActivitySwitch()}
              {editable && (
                <button
                  type="button"
                  onClick={() => {
                    this.setState({ editing: true });
                    this.handleEditClick();
                  }}
                  className={classNames(
                    "btn btn-secondary btn-sm",
                    deletable ? "mr5" : "",
                  )}
                >
                  <FontAwesomeIcon icon={["far", "fa-edit"]} />
                </button>
              )}
              {deletable && (
                <button
                  type="button"
                  onClick={this.onDelete}
                  className="btn btn-secondary btn-sm"
                >
                  <FontAwesomeIcon icon={["far", "fa-trash-alt"]} />
                </button>
              )}
            </div>
          </div>
          {content ?? this.renderView()}
          {this.renderWhoTimestamp()}
        </div>
      );
    }

    return (
      <div
        className={classNames(
          "timeline-activity",
          activity && activity.internal ? "private" : "public",
          deleting ? "fade-out" : "",
        )}
        ref={this.containerRef}
      >
        <span className="icon fa-stack fa-lg">
          <FontAwesomeIcon
            icon={["fas", "fa-circle"]}
            className={`fa-stack-2x ${color}`}
          />
          <FontAwesomeIcon
            icon={[`${prefix}`, `fa-${icon}`]}
            inverse
            className="fa-stack-1x"
          />
        </span>

        <div className="body pl-1">{body}</div>
      </div>
    );
  }
}

TimelineActivity.defaultProps = {
  editable: false,
  deletable: false,
  alwaysPublic: false,
  icon: "sticky-note",
  prefix: "far",
  color: "text-grey-dark",
  showReplyButtons: false,
  setCurrentFormWithData: () => undefined,
};

export default TimelineActivity;
